import type { FileUIPart } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { ChatPromptComposer, CHAT_MAX_UPLOAD_FILES, CHAT_MAX_UPLOAD_SIZE_BYTES } from "@/components/chat-prompt-composer";
import {
  PromptInputProvider,
  usePromptInputAttachments,
  usePromptInputController,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import { QuickLaunchTargetPicker } from "@/components/quick-launch-target-picker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Agent } from "@/hooks/use-agents";
import { useQuickLaunchRouting } from "@/hooks/use-quick-launch-routing";
import {
  toQuickLaunchAttachmentMetadata,
  transcribeQuickLaunchAudio,
  type QuickLaunchTarget,
} from "@/lib/quick-launch-api";
import type { AccessTokenGetter } from "@/lib/utils";

export interface QuickLaunchSubmission {
  target: QuickLaunchTarget;
  text: string;
  files: FileUIPart[];
}

export interface QuickLaunchDialogProps {
  open: boolean;
  onClose: () => void;
  /** Focus target captured by useQuickLaunch before this controlled dialog opens. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  getAccessToken: AccessTokenGetter;
  /** Resolves the saved user-preference default agent for no-match fallback. */
  getDefaultAgentId: () => Promise<string | null>;
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  /** Effective server-backed desktop send shortcut. */
  submitShortcut?: "enter" | "ctrl_enter";
  onLaunch: (submission: QuickLaunchSubmission) => Promise<void>;
}

type PromptFile = FileUIPart & { rawFile?: File };
type PendingLaunch = {
  message: PromptInputMessage;
  resolve: () => void;
  reject: (error: Error) => void;
};

function createQuickLaunchId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `ql-${crypto.randomUUID()}`;
  }
  return `ql-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function isAudioAttachment(file: FileUIPart): boolean {
  const type = (file.mediaType ?? "").toLowerCase();
  return type.startsWith("audio/") || type.startsWith("video/");
}

function QuickLaunchDialogBody({
  quickLaunchId,
  onClose,
  getAccessToken,
  getDefaultAgentId,
  agents,
  agentsLoading,
  agentsError,
  submitShortcut = "enter",
  onLaunch,
}: Omit<QuickLaunchDialogProps, "open"> & { quickLaunchId: string }) {
  const { t, i18n } = useTranslation();
  const controller = usePromptInputController();
  const attachments = usePromptInputAttachments();
  const [transcript, setTranscript] = useState("");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const [voiceAttempt, setVoiceAttempt] = useState<{ text: string; file: File } | null>(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [pendingLaunch, setPendingLaunch] = useState<PromptInputMessage | null>(null);
  const transcriptionAbortRef = useRef<AbortController | null>(null);
  const pendingLaunchRef = useRef<PendingLaunch | null>(null);
  const launchInFlightRef = useRef(false);
  const launchButtonRef = useRef<HTMLButtonElement>(null);
  const previousIsTranscribingRef = useRef(isTranscribing);

  // PromptInput clears text before invoking onSubmit. Keep a pending submit's
  // original draft in the routing input so that clear does not abort its route.
  const routingFiles = pendingLaunch?.files ?? attachments.files;
  const attachmentMetadata = useMemo(
    () => toQuickLaunchAttachmentMetadata(routingFiles as PromptFile[]),
    [routingFiles],
  );
  const routing = useQuickLaunchRouting({
    quickLaunchId,
    taskText: pendingLaunch?.text ?? controller.textInput.value,
    transcript,
    attachments: attachmentMetadata,
    getToken: getAccessToken,
  });

  useEffect(() => () => transcriptionAbortRef.current?.abort(), []);

  useEffect(() => {
    const wasTranscribing = previousIsTranscribingRef.current;
    previousIsTranscribingRef.current = isTranscribing;
    if (!wasTranscribing || isTranscribing) return;

    // Run after textarea focus work triggered by the completed transcription.
    const focusLaunchButton = () => launchButtonRef.current?.focus();
    if (typeof requestAnimationFrame === "undefined") {
      focusLaunchButton();
      return;
    }
    const frame = requestAnimationFrame(focusLaunchButton);
    return () => cancelAnimationFrame(frame);
  }, [isTranscribing]);

  const appendTranscript = useCallback((typedText: string, nextTranscript: string) => {
    const current = controller.textInput.value.trim() || typedText.trim();
    const cleanTranscript = nextTranscript.trim();
    if (!cleanTranscript) return current;
    if (current.includes(cleanTranscript)) return current;
    return current ? `${current}\n\n${cleanTranscript}` : cleanTranscript;
  }, [controller.textInput]);

  const transcribeVoiceAttempt = useCallback(async (attempt: { text: string; file: File }) => {
    transcriptionAbortRef.current?.abort();
    const abortController = new AbortController();
    transcriptionAbortRef.current = abortController;
    setVoiceAttempt(attempt);
    setIsTranscribing(true);
    setTranscriptionError(null);
    try {
      const response = await transcribeQuickLaunchAudio(
        attempt.file,
        i18n.language.split("-")[0],
        `quick-launch:${quickLaunchId}`,
        getAccessToken,
        abortController.signal,
      );
      if (abortController.signal.aborted) return;
      setTranscript(response.text);
      controller.textInput.setInput(appendTranscript(attempt.text, response.text));
    } catch (error: unknown) {
      if (abortController.signal.aborted) return;
      setTranscriptionError(error instanceof Error ? error.message : t("quickLaunch.transcriptionFailure"));
    } finally {
      if (!abortController.signal.aborted) setIsTranscribing(false);
    }
  }, [appendTranscript, controller.textInput, getAccessToken, i18n.language, quickLaunchId, t]);

  const handleVoiceNote = useCallback((
    text: string,
    file: File,
    existingFiles: FileUIPart[],
  ) => {
    // VoiceNoteButton clears attachments when it hands them over. Restore only
    // regular attachments; the recording itself is consumed here and must not
    // reach useAgnoChat for a second transcription after navigation.
    const regularFiles = existingFiles
      .filter((existing) => !isAudioAttachment(existing))
      .map((existing) => (existing as PromptFile).rawFile)
      .filter((existing): existing is File => existing instanceof File);
    if (regularFiles.length > 0) attachments.add(regularFiles);
    controller.textInput.setInput(text);
    void transcribeVoiceAttempt({ text, file });
  }, [attachments, controller.textInput, transcribeVoiceAttempt]);

  const resolveDefaultAgentTarget = useCallback(async (): Promise<QuickLaunchTarget | null> => {
    const defaultAgentId = (await getDefaultAgentId())?.trim();
    if (!defaultAgentId) return null;
    const defaultAgent = agents.find((agent) => agent.id === defaultAgentId);
    if (defaultAgent?.type === "team") return null;
    return {
      kind: "agent",
      id: defaultAgentId,
      name: defaultAgent?.name ?? defaultAgentId,
      description: defaultAgent?.description ?? "",
    };
  }, [agents, getDefaultAgentId]);

  const launchWithTarget = useCallback(async (
    message: PromptInputMessage,
    target: QuickLaunchTarget | null,
    fallbackToDefaultAgent: boolean,
  ) => {
    try {
      const resolvedTarget = target ?? (
        fallbackToDefaultAgent
          ? await resolveDefaultAgentTarget().catch(() => null)
          : null
      );
      if (!resolvedTarget) {
        throw new Error(t(
          fallbackToDefaultAgent
            ? "quickLaunch.defaultAgentUnavailable"
            : "quickLaunch.targetRequired",
        ));
      }
      await onLaunch({ target: resolvedTarget, text: message.text, files: message.files });
      onClose();
    } catch (error: unknown) {
      // PromptInput has already cleared typed text before its callback. Put it
      // back so a failed task/session creation is recoverable without losing a
      // draft; attachments are retained by PromptInput on a rejected submit.
      controller.textInput.setInput(message.text);
      const failure = error instanceof Error
        ? error
        : new Error(t("quickLaunch.creatingSessionTask"));
      setLaunchError(failure.message);
      throw failure;
    } finally {
      launchInFlightRef.current = false;
      setIsLaunching(false);
    }
  }, [controller.textInput, onClose, onLaunch, resolveDefaultAgentTarget, t]);

  const launch = useCallback((message: PromptInputMessage): Promise<void> => {
    // State updates are asynchronous, so retain a synchronous guard for a
    // double-click or a second keyboard submit in the same render.
    if (launchInFlightRef.current) return Promise.resolve();

    launchInFlightRef.current = true;
    setIsLaunching(true);
    setLaunchError(null);

    if (routing.status === "routing" && !routing.target) {
      return new Promise<void>((resolve, reject) => {
        pendingLaunchRef.current = { message, resolve, reject };
        setPendingLaunch(message);
      });
    }

    return launchWithTarget(
      message,
      routing.target,
      routing.status !== "idle" && !routing.target,
    );
  }, [launchWithTarget, routing.status, routing.target]);

  useEffect(() => {
    if (!pendingLaunch || routing.status === "routing") return;
    const pending = pendingLaunchRef.current;
    if (!pending) return;

    pendingLaunchRef.current = null;
    setPendingLaunch(null);
    void launchWithTarget(pending.message, routing.target, true).then(
      pending.resolve,
      pending.reject,
    );
  }, [launchWithTarget, pendingLaunch, routing.status, routing.target]);

  const launchCurrentDraft = () => {
    const files = attachments.files.map(({ id: _id, ...file }) => file as FileUIPart);
    void launch({ text: controller.textInput.value, files }).catch(() => {
      // The inline error state above is the recoverable UI for footer submits.
    });
  };

  const hasSubmitPayload = Boolean(controller.textInput.value.trim() || attachments.files.length);
  const routingInProgress = routing.status === "routing" && hasSubmitPayload;
  const hasErrorContent = routing.status === "error" || Boolean(transcriptionError) || Boolean(launchError);

  return (
    <div className="flex min-h-0 flex-col gap-2">
      {isTranscribing && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="quick-launch-transcription-progress">
          <Loader2 className="size-3 animate-spin" />
          {t("quickLaunch.transcriptionProgress")}
        </div>
      )}

      <ChatPromptComposer
        inputKey={quickLaunchId}
        placeholder={t("quickLaunch.inputPlaceholder")}
        inputAriaLabel={t("quickLaunch.inputPlaceholder")}
        onSubmit={launch}
        onVoiceNoteSubmit={handleVoiceNote}
        voiceDictationShortcutTarget="quick-launch"
        disabled={isLaunching || isTranscribing}
        submitShortcut={submitShortcut}
        // TEMPORARY: the dialog footer owns the visible launch action.
        hideSubmit
        // Kill the composer's built-in top border: standalone chat needs it as a
        // divider above the message list, but inside the dialog it's a stray line.
        className="gap-1 border-t-0 p-0 mr-2"
        textareaClassName="px-2"
        multiple
        maxFiles={CHAT_MAX_UPLOAD_FILES}
        maxFileSize={CHAT_MAX_UPLOAD_SIZE_BYTES}
      />

      {hasErrorContent && (
        <div data-testid="quick-launch-error-area" aria-live="polite" aria-atomic="true">
          {routing.status === "error" && (
            <p className="text-sm text-destructive">{t("quickLaunch.routingFailure")}</p>
          )}
          {transcriptionError && (
            <div className="flex items-center gap-2">
              <p className="text-sm text-destructive">{t("quickLaunch.transcriptionFailure")}</p>
              {voiceAttempt && (
                <Button type="button" variant="link" size="sm" className="h-auto p-0 text-sm text-destructive" onClick={() => void transcribeVoiceAttempt(voiceAttempt)}>
                  {t("quickLaunch.retryRouting")}
                </Button>
              )}
            </div>
          )}
          {launchError && <p className="text-sm text-destructive">{launchError}</p>}
        </div>
      )}

      {/* TEMPORARY: keep manual target controls beside the launch actions while pinned to test.l-9.net. */}
      <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:space-x-0">
        <QuickLaunchTargetPicker
          target={routing.target}
          agents={agents}
          agentsLoading={agentsLoading}
          agentsError={agentsError}
          getToken={getAccessToken}
          onSelect={routing.setManualTarget}
          onClear={routing.clearTarget}
        />
        <div className="flex shrink-0 justify-end gap-2">
          <Button type="button" variant="outline" className="h-8 py-0" onClick={onClose} disabled={isLaunching}>
            {t("quickLaunch.cancel")}
          </Button>
          <Button
            ref={launchButtonRef}
            type="button"
            className="h-8 py-0 focus-visible:bg-primary/80"
            onClick={launchCurrentDraft}
            disabled={!hasSubmitPayload || isLaunching || isTranscribing}
          >
            {(routingInProgress || isLaunching) && <Loader2 className="animate-spin" />}
            {t("quickLaunch.launch")}
          </Button>
        </div>
      </DialogFooter>
    </div>
  );
}

/** Cmd/Ctrl+/ dialog hosting an isolated normal chat composer. */
export function QuickLaunchDialog({ open, onClose, returnFocusRef, ...props }: QuickLaunchDialogProps) {
  const { t } = useTranslation();
  const [quickLaunchId, setQuickLaunchId] = useState("");

  useEffect(() => {
    setQuickLaunchId(open ? createQuickLaunchId() : "");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      {open && quickLaunchId && (
        <DialogContent
          hideCloseButton
          className="max-h-[90vh] w-[min(46rem,calc(100vw-2rem))] max-w-none gap-0 overflow-visible p-0"
          onCloseAutoFocus={(event) => {
            const returnFocusElement = returnFocusRef?.current;
            if (!returnFocusElement?.isConnected) return;
            event.preventDefault();
            returnFocusElement.focus();
          }}
        >
          {/*
            Keep normal dialog content scrollable without clipping the Projects
            popover, which portals directly into the Dialog scroll shard so
            Radix permits its wheel and touch scrolling.
          */}
          <div
            className="min-h-0 max-h-[calc(90vh-3rem)] overflow-y-auto p-2 sm:p-6"
            data-testid="quick-launch-dialog-scroll-container"
          >
            {/* TEMPORARY: retain dialog context for assistive technology without visual header chrome. */}
            <DialogTitle className="sr-only">{t("quickLaunch.title")}</DialogTitle>
            <DialogDescription className="sr-only">{t("quickLaunch.description")}</DialogDescription>
            <PromptInputProvider key={quickLaunchId}>
              <QuickLaunchDialogBody
                {...props}
                quickLaunchId={quickLaunchId}
                onClose={onClose}
              />
            </PromptInputProvider>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
